/**
 * Single source of truth for the recent-projects localStorage key.
 *
 * Deliberately a standalone leaf module (no other project imports). A
 * `RECENT_PROJECTS_KEY` re-export used to live directly in `project-slice.ts`,
 * but `auth-slice.ts` importing it from there hit a pre-existing circular
 * dependency landmine:
 *
 *   project-slice.ts -> shell/ide-v2/selection-context.ts ->
 *   features/page-editor/store/editor-store.ts ->
 *   features/page-editor/store/selection-slice.ts -> studio-store.ts
 *
 * `studio-store.ts` itself statically imports `project-slice.ts` (the normal,
 * safe direction — `studio-store.ts` is always the graph's entry point in the
 * running app). But when something OTHER than `studio-store.ts` becomes the
 * first module to import `project-slice.ts` (as a unit test importing it
 * directly does, and as `auth-slice.ts` would if it imported it statically),
 * the cycle reverses direction: `studio-store.ts` gets pulled in and evaluated
 * while `project-slice.ts` is still mid-initialization, and
 * `createProjectSlice` is not yet defined when `studio-store.ts`'s own
 * top-level `create()` call tries to spread it in — `TypeError:
 * createProjectSlice is not a function`. Reproduced directly: a test that
 * does nothing but `await import('../project-slice')` throws that exact
 * error today, independent of this change.
 *
 * Keeping the key (and the sign-out clear helper that uses it) in this
 * dependency-free leaf module lets `auth-slice.ts` reference it without
 * reaching into `project-slice.ts`'s module graph at all. Studio Wave 3 Lane
 * C, 2026-08-17 —
 * `docs/superpowers/specs/2026-08-17-studio-slice4-logout-walk.md`.
 */
export const RECENT_PROJECTS_KEY = 'cogo-studio-recent-projects'

/**
 * Sign-out storage hygiene: the recent-projects list identifies which of
 * THIS signed-out user's own projects were opened in this browser. On a
 * shared machine, a different account signing in afterward should not
 * inherit somebody else's recent-projects list. First caller:
 * `auth-slice.ts`'s `signOut()`.
 */
export function clearRecentProjectsOnSignOut(): void {
  try {
    localStorage.removeItem(RECENT_PROJECTS_KEY)
  } catch { /* ignore */ }
}
